/*
 * AuraRuby — behaviour layer (no dependencies)
 * - Scroll reveal:  [data-ar-reveal], staggered inside [data-ar-stagger]
 * - Parallax:       [data-ar-parallax="0.08"] (max 24 px)
 * - Buy bar:        [data-ar-buybar], shown between [data-ar-buy-start] and [data-ar-buy-end]
 *                   (on the product page: between the theme's add-to-cart form and the footer)
 * - Carousel:       [data-ar-carousel] with [data-ar-track], [data-ar-prev], [data-ar-next]
 * - Video:          video[data-ar-autoplay] and UGC videos play only while on screen;
 *                   [data-ar-video-toggle] lets people pause or play them
 * Re-initialises when a section is re-rendered in the theme editor.
 */
(function () {
  'use strict';

  window.AuraRubyReady = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hasIO = 'IntersectionObserver' in window;

  function each(root, selector, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(selector), fn);
  }

  /* Scroll reveal */
  var revealObserver = hasIO
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;

  function initReveal(root) {
    each(root, '[data-ar-stagger]', function (group) {
      var step = parseInt(group.getAttribute('data-ar-stagger'), 10) || 90;
      each(group, '[data-ar-reveal]', function (el, i) {
        el.style.setProperty('--ar-delay', i * step + 'ms');
      });
    });
    each(root, '[data-ar-reveal]', function (el) {
      if (!revealObserver || reduceMotion.matches) el.classList.add('is-visible');
      else revealObserver.observe(el);
    });
  }

  /* Parallax */
  var parallaxItems = [];
  var parallaxQueued = false;

  function updateParallax() {
    parallaxQueued = false;
    var vh = window.innerHeight;
    parallaxItems.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      var factor = parseFloat(el.getAttribute('data-ar-parallax')) || 0.08;
      var offset = (rect.top + rect.height / 2 - vh / 2) * -factor;
      offset = Math.max(-24, Math.min(24, offset));
      el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
    });
  }

  function queueParallax() {
    if (parallaxQueued || !parallaxItems.length) return;
    parallaxQueued = true;
    window.requestAnimationFrame(updateParallax);
  }

  function initParallax(root) {
    if (reduceMotion.matches) return;
    each(root, '[data-ar-parallax]', function (el) {
      if (parallaxItems.indexOf(el) === -1) parallaxItems.push(el);
    });
    queueParallax();
  }

  /* Buy bar */
  var buybar = null;
  var buybarQueued = false;

  function updateBuybar() {
    buybarQueued = false;
    if (!buybar || !document.body.contains(buybar)) return;
    var start = document.querySelector('[data-ar-buy-start]') || document.querySelector('main form[action$="/cart/add"]');
    var end = document.querySelector('[data-ar-buy-end]') || document.querySelector('footer');
    var vh = window.innerHeight;
    var pastStart = start ? start.getBoundingClientRect().bottom < 0 : window.scrollY > vh * 0.6;
    var beforeEnd = end ? end.getBoundingClientRect().top > vh : true;
    var show = pastStart && beforeEnd;
    if (show === buybar.classList.contains('is-visible')) return;
    buybar.classList.toggle('is-visible', show);
    buybar.inert = !show;
    if (show) buybar.removeAttribute('aria-hidden');
    else buybar.setAttribute('aria-hidden', 'true');
    // Keep keyboard focus from landing underneath the bar (WCAG 2.4.11).
    document.documentElement.style.scrollPaddingBottom = show ? buybar.offsetHeight + 16 + 'px' : '';
  }

  function queueBuybar() {
    if (buybarQueued || !buybar) return;
    buybarQueued = true;
    window.requestAnimationFrame(updateBuybar);
  }

  function initBuybar(root) {
    var found = root.querySelector('[data-ar-buybar]') || document.querySelector('[data-ar-buybar]');
    if (found) buybar = found;
    queueBuybar();
  }

  /* Carousel */
  function initCarousel(root) {
    each(root, '[data-ar-carousel]', function (carousel) {
      if (carousel.dataset.arReady) return;
      carousel.dataset.arReady = 'true';
      var track = carousel.querySelector('[data-ar-track]');
      var prev = carousel.querySelector('[data-ar-prev]');
      var next = carousel.querySelector('[data-ar-next]');
      if (!track || !prev || !next) return;

      function stepSize() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }
      // aria-disabled instead of disabled, so keyboard focus stays on the button at either end.
      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.setAttribute('aria-disabled', track.scrollLeft <= 2 ? 'true' : 'false');
        next.setAttribute('aria-disabled', track.scrollLeft >= max ? 'true' : 'false');
      }
      function go(direction, button) {
        if (button.getAttribute('aria-disabled') === 'true') return;
        track.scrollBy({ left: direction * stepSize(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
      prev.addEventListener('click', function () { go(-1, prev); });
      next.addEventListener('click', function () { go(1, next); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* Video: play only while visible, never with sound, and always pausable */
  function play(video) {
    var playing = video.play();
    if (playing && playing.catch) playing.catch(function () {});
  }

  function syncToggle(video) {
    var toggle = video.parentElement && video.parentElement.querySelector('[data-ar-video-toggle]');
    if (!toggle) return;
    var paused = video.paused;
    toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    toggle.classList.toggle('is-playing', !paused);
  }

  var videoObserver = hasIO
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          var autoplay = !reduceMotion.matches && video.dataset.arUserPaused !== 'true';
          if (entry.isIntersecting && autoplay) play(video);
          else if (!entry.isIntersecting) video.pause();
        });
      }, { threshold: 0.5 })
    : null;

  function initVideo(root) {
    each(root, 'video[data-ar-autoplay], .ar-ugc__video', function (video) {
      if (video.dataset.arReady) return;
      video.dataset.arReady = 'true';
      video.muted = true;
      video.addEventListener('play', function () { syncToggle(video); });
      video.addEventListener('pause', function () { syncToggle(video); });
      if (videoObserver) videoObserver.observe(video);
      syncToggle(video);
    });
    each(root, '[data-ar-video-toggle]', function (toggle) {
      if (toggle.dataset.arReady) return;
      toggle.dataset.arReady = 'true';
      toggle.addEventListener('click', function () {
        var video = toggle.parentElement.querySelector('video');
        if (!video) return;
        if (video.paused) {
          video.dataset.arUserPaused = 'false';
          play(video);
        } else {
          video.dataset.arUserPaused = 'true';
          video.pause();
        }
      });
    });
  }

  function init(root) {
    initReveal(root);
    initParallax(root);
    initCarousel(root);
    initVideo(root);
    initBuybar(root);
  }

  window.addEventListener('scroll', function () { queueParallax(); queueBuybar(); }, { passive: true });
  window.addEventListener('resize', function () { queueParallax(); queueBuybar(); }, { passive: true });
  window.addEventListener('load', queueBuybar);

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
})();
